import React from 'react';
import { useTranslation } from 'react-i18next';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Badge, State, Sub, Table } from '../../performance-operations/styles';
import { formatNumber, usePanel } from '../../performance-operations/config';
import Button from '../../../components/ui/Button';
import { formatDateTime } from '../../../utils/Utils';
import { fetchEvidence } from './config';

// Analiz E08 orta sağ — seçili çalışanın hedef, yetkinlik ve değerlendirme kanıtları + taşıma geçmişi + inceleme notu. Yetkisiz
// alan gösterilmez: 360 (akran/ast) yanıtları kişi bazında yok; kendi kaydını kimse açamaz (sunucu 403 → panel hata durumu).
// "İnceleme Notu" düğmesi not çekmecesini açar (puan değiştirmez; öneri ızgaradaki gerekçeli taşımayla).
export default function EvidencePanel({ sessionId, recordId, refreshKey, onReview, canReview }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const panel = usePanel(() => fetchEvidence(sessionId, recordId), [sessionId, recordId, refreshKey], !!recordId);
  const e = panel.data;
  const n = (v) => (v == null ? '-' : formatNumber(v, lang));

  if (!recordId) {
    return (
      <PanelCard panelKey="evidence" title={t('performance.calibrationWorkspace.panel.evidence')} span={4}>
        <State data-state="empty">{t('performance.calibrationWorkspace.evidence.pick')}</State>
      </PanelCard>
    );
  }
  return (
    <PanelCard
      panelKey="evidence" title={e?.employeeName || t('performance.calibrationWorkspace.panel.evidence')} span={4}
      loading={panel.loading} error={panel.error} onRetry={panel.retry}
      actions={e && canReview ? <Button variant="outline" onClick={() => onReview(e)} data-action="review">{t('performance.calibrationWorkspace.review.edit')}</Button> : null}
    >
      {e ? (
        <>
          <Table>
            <tbody>
              <tr><td>{t('performance.calibrationWorkspace.col.before')}</td><td>{e.beforeLabel || '-'}</td></tr>
              <tr><td>{t('performance.calibrationWorkspace.col.proposed')}</td><td>{e.proposedLabel || '-'}</td></tr>
              <tr><td>{t('performance.calibrationWorkspace.col.approved')}</td><td>{e.approvedLabel || '-'}</td></tr>
              <tr><td>{t('performance.calibrationWorkspace.evidence.systemA')}</td><td>{n(e.systemAScore)}</td></tr>
              <tr><td>{t('performance.calibrationWorkspace.evidence.systemB')}</td><td>{n(e.systemBScore)}</td></tr>
            </tbody>
          </Table>

          <Sub>{t('performance.calibrationWorkspace.evidence.goals')}</Sub>
          {e.goals.length === 0 ? <Sub>{t('performance.calibrationWorkspace.evidence.noGoals')}</Sub> : (
            <Table data-section="goals">
              <thead>
                <tr>
                  <th>{t('performance.calibrationWorkspace.evidence.goal')}</th>
                  <th>{t('performance.calibrationWorkspace.evidence.weight')}</th>
                  <th>{t('performance.calibrationWorkspace.evidence.targetActual')}</th>
                </tr>
              </thead>
              <tbody>
                {e.goals.map((g, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={i}>
                    <td title={g.title}>{g.title}</td>
                    <td>{g.weightPct == null ? '-' : `${formatNumber(g.weightPct, lang)}%`}</td>
                    <td>{g.kpiTargetValue == null ? '-' : `${n(g.kpiActualValue)} / ${n(g.kpiTargetValue)}`}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <Sub>{t('performance.calibrationWorkspace.evidence.competencies')}</Sub>
          {e.competencies.length === 0 ? <Sub>{t('performance.calibrationWorkspace.evidence.noCompetencies')}</Sub> : (
            <Table data-section="competencies">
              <thead>
                <tr>
                  <th>{t('performance.calibrationWorkspace.evidence.competency')}</th>
                  <th>{t('performance.calibrationWorkspace.evidence.manager')}</th>
                  <th>{t('performance.calibrationWorkspace.evidence.self')}</th>
                </tr>
              </thead>
              <tbody>
                {e.competencies.map((c) => (
                  <tr key={c.competencyId}>
                    <td title={c.name}>{c.name}</td>
                    <td>{n(c.managerAvg)}</td>
                    <td>{n(c.selfAvg)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <Sub>{t('performance.calibrationWorkspace.evidence.history')}</Sub>
          {e.history.length === 0 ? <Sub>{t('performance.calibrationWorkspace.evidence.noHistory')}</Sub> : (
            <Table data-section="history">
              <tbody>
                {e.history.map((h, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={i}>
                    <td>{`${h.fromLabel || '-'} → ${h.toLabel}`}</td>
                    <td title={h.note}>{h.note}</td>
                    <td>{`${h.movedByName || '-'} · ${formatDateTime(h.at, lang)}`}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <Sub>{t('performance.calibrationWorkspace.evidence.reviewNote')}</Sub>
          <div data-section="review">
            <Badge $tone={e.reviewStatus === 'reviewed' ? 'success' : e.reviewStatus === 'discuss' ? 'danger' : undefined}>
              {t(`performance.calibrationWorkspace.reviewStatus.${e.reviewStatus}`)}
            </Badge>
            {' '}
            {e.evidenceReviewed ? <Badge $tone="info">{t('performance.calibrationWorkspace.evidenceReviewed')}</Badge> : null}
            {e.reviewNote ? <Sub title={e.reviewNote}>{e.reviewNote}</Sub> : null}
            {e.reviewedByName ? <Sub>{`${e.reviewedByName} · ${formatDateTime(e.reviewedAt, lang)}`}</Sub> : null}
          </div>
        </>
      ) : null}
    </PanelCard>
  );
}
